// services/aiUsageService.ts
// Firestore queries for broad AI usage tracking across all app features

import {
    collection,
    query,
    where,
    getDocs,
    orderBy
} from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
    AIUsageDailyAnalytics,
    AIUsageAggregatedAnalytics,
    CombinedAIDashboard,
    ChartData,
    TrendData,
    CostData
} from '../types/AIUsageTypes';
import { AIFeatureCategoryInfo } from '../types/AIUsageTypes';
import type { OmoreInteractionDailyAnalytics } from '../types/OmoreInteractionTypes';

// ─── Collection Paths ────────────────────────────────────────

const AI_ANALYTICS_DAILY_PATH = 'aiUsage/analytics/daily';
const OMORE_ANALYTICS_DAILY_PATH = 'omoreInteractions/analytics/daily';
const OMORE_MODERATION_FLAGGED_PATH = 'omoreInteractions/moderation/flagged';
const OMORE_MODERATION_USERS_PATH = 'omoreInteractions/moderation/users';

// ─── Helpers ─────────────────────────────────────────────────

/** Format a Date as YYYY-MM-DD */
function formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
}

/** Get a Date shifted by N days from today */
function daysAgo(n: number): Date {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
}

/** Merge two Record<string, number> by summing values */
function mergeRecords(a: Record<string, number>, b: Record<string, number>): Record<string, number> {
    const result = { ...a };
    for (const [key, val] of Object.entries(b)) {
        result[key] = (result[key] || 0) + val;
    }
    return result;
}

/** Union two string arrays (deduplicated) */
function unionArrays(a: string[], b: string[]): string[] {
    return [...new Set([...a, ...b])];
}

// ─── AI Usage Analytics ──────────────────────────────────────

/**
 * Fetch AI daily analytics docs for a date range and aggregate
 */
export async function fetchAIUsageAnalytics(
    startDate: string,
    endDate: string
): Promise<AIUsageAggregatedAnalytics> {
    try {
        const q = query(
            collection(db, AI_ANALYTICS_DAILY_PATH),
            where('date', '>=', startDate),
            where('date', '<=', endDate),
            orderBy('date', 'asc')
        );
        const snapshot = await getDocs(q);

        const dailyStats: AIUsageDailyAnalytics[] = snapshot.docs.map(d => {
            const data = d.data();
            return {
                date: data.date || d.id,
                totalCalls: data.totalCalls || 0,
                uniqueUserIDs: data.uniqueUserIDs || [],
                totalTokens: data.totalTokens || 0,
                callsByCategory: data.callsByCategory || {},
                tokensByCategory: data.tokensByCategory || {},
                callsByModel: data.callsByModel || {},
                failedCalls: data.failedCalls || 0,
                flaggedCalls: data.flaggedCalls || 0,
                totalResponseTimeMs: data.totalResponseTimeMs || 0,
                callsWithResponseTime: data.callsWithResponseTime || 0,
                callsByDevice: data.callsByDevice || {},
            };
        });

        // Aggregate
        const totals = dailyStats.reduce(
            (acc, day) => ({
                totalCalls: acc.totalCalls + day.totalCalls,
                uniqueUserIDs: unionArrays(acc.uniqueUserIDs, day.uniqueUserIDs),
                totalTokens: acc.totalTokens + day.totalTokens,
                callsByCategory: mergeRecords(acc.callsByCategory, day.callsByCategory),
                tokensByCategory: mergeRecords(acc.tokensByCategory, day.tokensByCategory),
                callsByModel: mergeRecords(acc.callsByModel, day.callsByModel),
                failedCalls: acc.failedCalls + day.failedCalls,
                flaggedCalls: acc.flaggedCalls + day.flaggedCalls,
                totalResponseTimeMs: acc.totalResponseTimeMs + day.totalResponseTimeMs,
                callsWithResponseTime: acc.callsWithResponseTime + day.callsWithResponseTime,
            }),
            {
                totalCalls: 0,
                uniqueUserIDs: [] as string[],
                totalTokens: 0,
                callsByCategory: {} as Record<string, number>,
                tokensByCategory: {} as Record<string, number>,
                callsByModel: {} as Record<string, number>,
                failedCalls: 0,
                flaggedCalls: 0,
                totalResponseTimeMs: 0,
                callsWithResponseTime: 0,
            }
        );

        const dayCount = dailyStats.length || 1;

        return {
            startDate,
            endDate,
            totalCalls: totals.totalCalls,
            uniqueUsers: totals.uniqueUserIDs.length,
            totalTokens: totals.totalTokens,
            avgCallsPerDay: totals.totalCalls / dayCount,
            avgResponseTimeMs: totals.callsWithResponseTime > 0
                ? totals.totalResponseTimeMs / totals.callsWithResponseTime
                : 0,
            callsByCategory: totals.callsByCategory,
            tokensByCategory: totals.tokensByCategory,
            callsByModel: totals.callsByModel,
            failureRate: totals.totalCalls > 0 ? (totals.failedCalls / totals.totalCalls) * 100 : 0,
            flagRate: totals.totalCalls > 0 ? (totals.flaggedCalls / totals.totalCalls) * 100 : 0,
            dailyStats,
        };
    } catch (error) {
        console.error('Error fetching AI usage analytics:', error);
        return {
            startDate,
            endDate,
            totalCalls: 0,
            uniqueUsers: 0,
            totalTokens: 0,
            avgCallsPerDay: 0,
            avgResponseTimeMs: 0,
            callsByCategory: {},
            tokensByCategory: {},
            callsByModel: {},
            failureRate: 0,
            flagRate: 0,
            dailyStats: [],
        };
    }
}

// ─── Dashboard ───────────────────────────────────────────────

/**
 * Fetch the combined AI + Omore dashboard summary
 */
export async function fetchAIUsageDashboard(): Promise<CombinedAIDashboard> {
    const today = formatDate(new Date());
    const weekAgo = formatDate(daysAgo(7));

    try {
        // Fetch AI daily analytics for today and last 7 days in parallel
        const [todayAISnap, weekAISnap, todayOmoreSnap, weekOmoreSnap, pendingFlagsSnap, flaggedUsersSnap] =
            await Promise.all([
                getDocs(query(collection(db, AI_ANALYTICS_DAILY_PATH), where('date', '==', today))),
                getDocs(query(
                    collection(db, AI_ANALYTICS_DAILY_PATH),
                    where('date', '>=', weekAgo),
                    where('date', '<=', today),
                    orderBy('date', 'asc')
                )),
                getDocs(query(collection(db, OMORE_ANALYTICS_DAILY_PATH), where('date', '==', today))),
                getDocs(query(
                    collection(db, OMORE_ANALYTICS_DAILY_PATH),
                    where('date', '>=', weekAgo),
                    where('date', '<=', today),
                    orderBy('date', 'asc')
                )),
                getDocs(query(
                    collection(db, OMORE_MODERATION_FLAGGED_PATH),
                    where('reviewAction', '==', null)
                )),
                getDocs(query(
                    collection(db, OMORE_MODERATION_USERS_PATH),
                    where('totalFlags', '>', 0)
                )),
            ]);

        // ── AI Usage section ──
        const todayAI: AIUsageDailyAnalytics | null = todayAISnap.empty ? null : (() => {
            const d = todayAISnap.docs[0].data();
            return {
                date: d.date || today,
                totalCalls: d.totalCalls || 0,
                uniqueUserIDs: d.uniqueUserIDs || [],
                totalTokens: d.totalTokens || 0,
                callsByCategory: d.callsByCategory || {},
                tokensByCategory: d.tokensByCategory || {},
                callsByModel: d.callsByModel || {},
                failedCalls: d.failedCalls || 0,
                flaggedCalls: d.flaggedCalls || 0,
                totalResponseTimeMs: d.totalResponseTimeMs || 0,
                callsWithResponseTime: d.callsWithResponseTime || 0,
                callsByDevice: d.callsByDevice || {},
            };
        })();

        const weekAIStats: AIUsageDailyAnalytics[] = weekAISnap.docs.map(d => {
            const data = d.data();
            return {
                date: data.date || d.id,
                totalCalls: data.totalCalls || 0,
                uniqueUserIDs: data.uniqueUserIDs || [],
                totalTokens: data.totalTokens || 0,
                callsByCategory: data.callsByCategory || {},
                tokensByCategory: data.tokensByCategory || {},
                callsByModel: data.callsByModel || {},
                failedCalls: data.failedCalls || 0,
                flaggedCalls: data.flaggedCalls || 0,
                totalResponseTimeMs: data.totalResponseTimeMs || 0,
                callsWithResponseTime: data.callsWithResponseTime || 0,
                callsByDevice: data.callsByDevice || {},
            };
        });

        const weekAITotals = weekAIStats.reduce(
            (acc, day) => ({
                calls: acc.calls + day.totalCalls,
                tokens: acc.tokens + day.totalTokens,
                failed: acc.failed + day.failedCalls,
            }),
            { calls: 0, tokens: 0, failed: 0 }
        );

        // Find top category by calls
        const categoryCalls = todayAI?.callsByCategory || {};
        const topCategory = Object.entries(categoryCalls).sort((a, b) => b[1] - a[1])[0]?.[0] || 'N/A';

        // Build category breakdown from week totals
        const weekCallsByCategory = weekAIStats.reduce(
            (acc, day) => mergeRecords(acc, day.callsByCategory),
            {} as Record<string, number>
        );
        const weekTokensByCategory = weekAIStats.reduce(
            (acc, day) => mergeRecords(acc, day.tokensByCategory),
            {} as Record<string, number>
        );
        const allCategories = new Set([...Object.keys(weekCallsByCategory), ...Object.keys(weekTokensByCategory)]);
        const categoryBreakdown = Array.from(allCategories).map(cat => ({
            category: cat,
            calls: weekCallsByCategory[cat] || 0,
            tokens: weekTokensByCategory[cat] || 0,
            color: (AIFeatureCategoryInfo as any)[cat]?.color || '#718096',
        })).sort((a, b) => b.calls - a.calls);

        // Trends
        const callsTrend = weekAIStats.map(day => ({ date: day.date, count: day.totalCalls }));
        const tokensTrend = weekAIStats.map(day => ({ date: day.date, count: day.totalTokens }));

        // ── Omore section ──
        const todayOmoreData: OmoreInteractionDailyAnalytics | null = todayOmoreSnap.empty ? null : (() => {
            const d = todayOmoreSnap.docs[0].data();
            return {
                date: d.date || today,
                totalSessions: d.totalSessions || 0,
                totalMessages: d.totalMessages || 0,
                uniqueUsers: d.uniqueUsers || 0,
                sessionsByHelperType: d.sessionsByHelperType || {},
                sessionsByOrigin: d.sessionsByOrigin || {},
                totalSuggestionsShown: d.totalSuggestionsShown || 0,
                totalSuggestionsAdopted: d.totalSuggestionsAdopted || 0,
                avgSessionDurationSeconds: d.avgSessionDurationSeconds || 0,
                totalTokensUsed: d.totalTokensUsed || 0,
                flaggedMessageCount: d.flaggedMessageCount || 0,
            };
        })();

        // Week Omore totals for suggestion adoption
        const weekOmoreStats = weekOmoreSnap.docs.map(d => {
            const data = d.data();
            return {
                suggestionsShown: data.totalSuggestionsShown || 0,
                suggestionsAdopted: data.totalSuggestionsAdopted || 0,
                sessionsByHelperType: data.sessionsByHelperType || {} as Record<string, number>,
            };
        });
        const weekSuggestionsShown = weekOmoreStats.reduce((s, d) => s + d.suggestionsShown, 0);
        const weekSuggestionsAdopted = weekOmoreStats.reduce((s, d) => s + d.suggestionsAdopted, 0);
        const suggestionAdoptionRate = weekSuggestionsShown > 0
            ? Math.round((weekSuggestionsAdopted / weekSuggestionsShown) * 100)
            : 0;

        // Top helper type this week
        const weekHelperTypes = weekOmoreStats.reduce(
            (acc, d) => mergeRecords(acc, d.sessionsByHelperType),
            {} as Record<string, number>
        );
        const topHelperType = Object.entries(weekHelperTypes).sort((a, b) => b[1] - a[1])[0]?.[0] || 'general';

        // Sessions trend
        const sessionsTrend = weekOmoreSnap.docs.map(d => ({
            date: d.data().date || d.id,
            count: d.data().totalSessions || 0,
        }));

        return {
            aiUsage: {
                todayCalls: todayAI?.totalCalls || 0,
                todayTokens: todayAI?.totalTokens || 0,
                todayUniqueUsers: todayAI?.uniqueUserIDs?.length || 0,
                weekCalls: weekAITotals.calls,
                weekTokens: weekAITotals.tokens,
                topCategory,
                failureRate: weekAITotals.calls > 0 ? (weekAITotals.failed / weekAITotals.calls) * 100 : 0,
                callsTrend,
                tokensTrend,
                categoryBreakdown,
            },
            omoreInteractions: {
                todaySessions: todayOmoreData?.totalSessions || 0,
                todayMessages: todayOmoreData?.totalMessages || 0,
                pendingModeration: pendingFlagsSnap.size,
                usersWithFlags: flaggedUsersSnap.size,
                suggestionAdoptionRate,
                topHelperType,
                sessionsTrend,
            },
        };
    } catch (error) {
        console.error('Error fetching AI usage dashboard:', error);
        return {
            aiUsage: {
                todayCalls: 0, todayTokens: 0, todayUniqueUsers: 0,
                weekCalls: 0, weekTokens: 0, topCategory: 'N/A', failureRate: 0,
                callsTrend: [], tokensTrend: [], categoryBreakdown: [],
            },
            omoreInteractions: {
                todaySessions: 0, todayMessages: 0, pendingModeration: 0,
                usersWithFlags: 0, suggestionAdoptionRate: 0, topHelperType: 'general', sessionsTrend: [],
            },
        };
    }
}

// ─── Chart helpers ───────────────────────────────────────────

/**
 * Produce ChartData array for category distribution pie/bar
 */
export function getCategoryDistribution(analytics: AIUsageAggregatedAnalytics): ChartData[] {
    return Object.entries(analytics.callsByCategory)
        .map(([cat, calls]) => ({
            label: (AIFeatureCategoryInfo as any)[cat]?.displayName || cat,
            value: calls,
            color: (AIFeatureCategoryInfo as any)[cat]?.color || '#718096',
            icon: (AIFeatureCategoryInfo as any)[cat]?.icon,
        }))
        .sort((a, b) => b.value - a.value);
}

/**
 * Produce ChartData array for model distribution
 */
export function getModelDistribution(analytics: AIUsageAggregatedAnalytics): ChartData[] {
    const modelColors: Record<string, string> = {
        'gpt-4o-mini': '#3b82f6',
        'gpt-4o': '#1d4ed8',
        'dall-e-2': '#10b981',
        'dall-e-3': '#059669',
        'tts-1': '#8b5cf6',
        'tts-1-hd': '#7c3aed',
        'text-moderation-latest': '#64748b',
        'unknown': '#9ca3af',
    };

    return Object.entries(analytics.callsByModel)
        .map(([model, calls]) => ({
            label: model,
            value: calls,
            color: modelColors[model] || '#9ca3af',
        }))
        .sort((a, b) => b.value - a.value);
}

/**
 * Produce TrendData array for token consumption over time
 */
export function getTokenConsumptionTrend(analytics: AIUsageAggregatedAnalytics): TrendData[] {
    return analytics.dailyStats.map(day => ({
        date: day.date,
        value: day.totalTokens,
    }));
}

/**
 * Produce cost breakdown per category
 */
export function getCostBreakdown(analytics: AIUsageAggregatedAnalytics): CostData[] {
    const allCategories = new Set([
        ...Object.keys(analytics.callsByCategory),
        ...Object.keys(analytics.tokensByCategory)
    ]);

    return Array.from(allCategories)
        .map(cat => ({
            category: cat,
            displayName: (AIFeatureCategoryInfo as any)[cat]?.displayName || cat,
            totalTokens: analytics.tokensByCategory[cat] || 0,
            totalCalls: analytics.callsByCategory[cat] || 0,
            color: (AIFeatureCategoryInfo as any)[cat]?.color || '#718096',
        }))
        .sort((a, b) => b.totalTokens - a.totalTokens);
}
